import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Award, Shield, TrendingUp, Star, CheckCircle, Briefcase, Search } from 'lucide-react';
import { CandidateBadgeDashboard, CandidateBadge } from '../components/CandidateBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const BADGE_CATEGORIES = [
  { id: 'construction-property', name: 'Construction', icon: '🏗️' },
  { id: 'manufacturing-warehouse', name: 'Warehouse', icon: '🏭' },
  { id: 'logistics-transport', name: 'Logistics', icon: '🚚' },
  { id: 'creative-media', name: 'Creative', icon: '🎨' },
  { id: 'freelance-gigs', name: 'Freelance', icon: '💼' },
  { id: 'hospitality', name: 'Hospitality', icon: '🏨' },
  { id: 'retail', name: 'Retail', icon: '🛍️' },
  { id: 'security', name: 'Security', icon: '🛡️' },
];

const CandidateBadgesPage = () => {
  const [searchParams] = useSearchParams();
  const [candidateEmail, setCandidateEmail] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'construction-property');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [subcategories, setSubcategories] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    // Check if user is logged in
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      setCandidateEmail(userData.email);
      setIsLoggedIn(true);
    }
    
    fetchAllBadges();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchLeaderboard(selectedCategory);
      fetchSubcategories(selectedCategory);
      setSelectedSubcategory('all'); // Reset subcategory when category changes
    }
  }, [selectedCategory]);

  const fetchAllBadges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/definitions`);
      const data = await response.json();
      setAllBadges(data.badges || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSubcategories = async (category) => {
    try {
      const response = await fetch(`${API_URL}/api/job-services/subcategories/${category}`);
      const data = await response.json();
      if (data.success && data.has_subcategories) {
        setSubcategories(data.subcategories || []);
      } else {
        setSubcategories([]);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const fetchLeaderboard = async (category) => {
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/leaderboard/${category}?limit=5`);
      const data = await response.json();
      setLeaderboard(data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  // Filter badges by subcategory
  const getFilteredBadges = (badges, category) => {
    const categoryBadges = badges.filter((b) => b.category === category);
    if (selectedSubcategory === 'all' || subcategories.length === 0) {
      return categoryBadges;
    }
    // Filter by subcategory - show badges that match or have no subcategory (general badges)
    if (selectedSubcategory === 'general') {
      return categoryBadges.filter((b) => !b.subcategory);
    }
    return categoryBadges.filter((b) => b.subcategory === selectedSubcategory);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (candidateEmail) {
      setIsLoggedIn(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="candidate-badges-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Award className="w-12 h-12" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Candidate Badges</h1>
              <p className="text-amber-100 text-lg">Stand out to employers with verified skill badges</p>
            </div>
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="text-3xl font-bold">{stats.total_badges_awarded || 0}</div>
                <div className="text-amber-100 text-sm">Badges Awarded</div>
              </div>
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="text-3xl font-bold">{stats.total_badge_types || 0}</div>
                <div className="text-amber-100 text-sm">Badge Types</div>
              </div>
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="text-3xl font-bold">{BADGE_CATEGORIES.length}</div>
                <div className="text-amber-100 text-sm">Categories</div>
              </div>
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="text-3xl font-bold">30%</div>
                <div className="text-amber-100 text-sm">Max Search Boost</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Login/Email Section */}
        {!isLoggedIn && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                Check Your Badge Eligibility
              </CardTitle>
              <CardDescription>
                Enter your email to see which badges you've earned and which ones you're eligible for
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailSubmit} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="email" className="sr-only">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={candidateEmail}
                    onChange={(e) => setCandidateEmail(e.target.value)}
                    className="w-full"
                    data-testid="badge-email-input"
                  />
                </div>
                <Button type="submit" data-testid="check-badges-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Check Badges
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Badge Dashboard */}
          <div className="lg:col-span-2">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList className="grid grid-cols-4 md:grid-cols-8 mb-6">
                {BADGE_CATEGORIES.map((cat) => (
                  <TabsTrigger key={cat.id} value={cat.id} className="text-xs md:text-sm">
                    <span className="mr-1">{cat.icon}</span>
                    <span className="hidden md:inline">{cat.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {BADGE_CATEGORIES.map((cat) => (
                <TabsContent key={cat.id} value={cat.id}>
                  {isLoggedIn ? (
                    <CandidateBadgeDashboard
                      candidateEmail={candidateEmail}
                      category={cat.id}
                    />
                  ) : (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <span>{cat.icon}</span>
                        {cat.name} Badges
                      </h3>
                      
                      {/* Sub-category Filter - Only show if category has subcategories */}
                      {subcategories.length > 0 && selectedCategory === cat.id && (
                        <div className="flex flex-wrap gap-2 pb-2" data-testid="subcategory-filter">
                          <button
                            onClick={() => setSelectedSubcategory('all')}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              selectedSubcategory === 'all'
                                ? 'bg-amber-500 text-white shadow-md'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            data-testid="subcategory-all"
                          >
                            All ({allBadges.filter(b => b.category === cat.id).length})
                          </button>
                          <button
                            onClick={() => setSelectedSubcategory('general')}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              selectedSubcategory === 'general'
                                ? 'bg-amber-500 text-white shadow-md'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            data-testid="subcategory-general"
                          >
                            ⭐ General ({allBadges.filter(b => b.category === cat.id && !b.subcategory).length})
                          </button>
                          {subcategories.map((sub) => {
                            const count = allBadges.filter(b => b.category === cat.id && b.subcategory === sub.id).length;
                            return (
                              <button
                                key={sub.id}
                                onClick={() => setSelectedSubcategory(sub.id)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                  selectedSubcategory === sub.id
                                    ? 'bg-amber-500 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                                data-testid={`subcategory-${sub.id}`}
                              >
                                {sub.icon} {sub.name} ({count})
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <div className="grid gap-4 md:grid-cols-2">
                        {getFilteredBadges(allBadges, cat.id).map((badge) => (
                            <Card key={badge.id} className="relative overflow-hidden">
                              {badge.subcategory && (
                                <div className="absolute top-2 right-2">
                                  <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full">
                                    {subcategories.find(s => s.id === badge.subcategory)?.name || badge.subcategory}
                                  </span>
                                </div>
                              )}
                              <CardHeader className="pb-2">
                                <div className="flex items-center gap-3">
                                  <div className="text-3xl">{badge.icon}</div>
                                  <div>
                                    <CardTitle className="text-base">{badge.name}</CardTitle>
                                    <CardDescription className="text-xs">{badge.description}</CardDescription>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-xs text-gray-500 space-y-1">
                                  {badge.benefits?.slice(0, 2).map((benefit, idx) => (
                                    <div key={idx} className="flex items-start gap-1">
                                      <Star className="w-3 h-3 text-amber-500 mt-0.5" />
                                      {benefit}
                                    </div>
                                  ))}
                                </div>
                                {badge.search_boost > 1 && (
                                  <div className="mt-2 flex items-center gap-1 text-xs text-emerald-600">
                                    <TrendingUp className="w-3 h-3" />
                                    {Math.round((badge.search_boost - 1) * 100)}% visibility boost
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        {getFilteredBadges(allBadges, cat.id).length === 0 && (
                          <div className="col-span-2 text-center py-8 text-gray-400">
                            No badges found for this filter
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* How It Works */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-500" />
                  How Badges Work
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium text-sm">Complete Services</p>
                    <p className="text-xs text-gray-500">Use our career services like CV review or interview prep</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium text-sm">Earn Badges</p>
                    <p className="text-xs text-gray-500">Automatically qualify for badges based on completed services</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium text-sm">Stand Out</p>
                    <p className="text-xs text-gray-500">Badges appear on your profile and boost your visibility in search</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Category Leaderboard */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  Top Badge Earners
                </CardTitle>
                <CardDescription>
                  {BADGE_CATEGORIES.find(c => c.id === selectedCategory)?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="space-y-3">
                    {leaderboard.map((entry, idx) => (
                      <div key={entry.candidate_email} className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-100 text-amber-600' :
                          idx === 1 ? 'bg-gray-100 text-gray-600' :
                          idx === 2 ? 'bg-orange-100 text-orange-600' :
                          'bg-gray-50 text-gray-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {entry.candidate_email.split('@')[0]}***
                          </p>
                          <div className="flex gap-1">
                            {entry.badges.slice(0, 3).map((b) => (
                              <span key={b.id} title={b.name}>{b.icon}</span>
                            ))}
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {entry.badge_count} {entry.badge_count === 1 ? 'badge' : 'badges'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Be the first to earn a badge!
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Benefits Overview */}
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-emerald-800">
                  <CheckCircle className="w-5 h-5" />
                  Badge Benefits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  Higher visibility in search results
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  Badge displayed on your profile
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  Priority matching for relevant jobs
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  Shows employers your commitment
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateBadgesPage;
